import React from "react";

import * as Sentry from "@sentry/react";
import { Button, Result } from "antd";

import { safeSessionStorage } from "./safeStorage";

/**
 * Recuperacion de chunks lazy que ya no existen en el servidor (MB-APP-EXPRESS-167:
 * "Unable to preload CSS for /assets/ContractEditorPage-*.css").
 *
 * Que pasa hoy: la pestaña tiene abierto un `index.html` viejo que nombra assets con
 * hash de una release anterior. Tras un deploy, Firebase solo sirve el manifiesto de la
 * release actual y su rewrite `** -> /index.html` hace que la peticion del CSS viejo
 * devuelva 200 con `text/html` en vez de 404. El `<link rel=stylesheet>` que crea
 * `__vitePreload` rechaza ese MIME, dispara `error` y Vite rechaza el `import()` con
 * `Unable to preload CSS for <dep>`. `React.lazy` lo re-lanza en el render y el error
 * sube al `Sentry.ErrorBoundary` de `App.tsx`: issue en Sentry + modal de feedback.
 *
 * Por que NO usamos la receta habitual de `vite:preloadError` + `preventDefault()`:
 * si nadie cancela el evento, `handlePreloadError` de Vite hace `throw err` y ese
 * rechazo es justo lo que este helper necesita atrapar. Con `preventDefault()`, Vite
 * NO lanza y `__vitePreload` continua a `baseModule().catch(handlePreloadError)`; si el
 * chunk JS tambien falta (lo normal en un deploy viejo) ese catch tampoco lanza y el
 * `import()` RESUELVE `undefined`. React 19 lee `.default` de eso sin comprobar nada,
 * asi que cambia este issue por un `TypeError` nuevo y con mensaje inutil
 * ("Cannot use 'in' operator to search for 'default' in undefined" en 19.2).
 *
 * Contra eso hay DOS defensas, y la segunda es la que de verdad cierra el agujero:
 *
 *  1. Nadie en el repo toca ese evento. Lo vigila el test "invariante de repo: nadie
 *     toca vite:preloadError", que BARRE EL TEXTO de `src/` y de `index.html` con
 *     `findPreloadErrorReferences`. La guarda anterior era placebo: despachaba un evento
 *     sintetico contra el `window` de happy-dom desde un archivo de test que no carga
 *     ningun modulo de la app, asi que no habia oyentes posibles y pasaba en verde
 *     incluso con el `preventDefault()` pegado en `src/main.tsx`.
 *
 *  2. RED DE SEGURIDAD en runtime, que no depende de ninguna convencion: si la fabrica
 *     resuelve `null`/`undefined`, este helper NO se lo devuelve a React — lo trata como
 *     chunk ausente y entra al mismo camino de recarga/aviso. Asi el `TypeError` deja de
 *     ser alcanzable aunque alguien cancele el evento manaña.
 *
 * Regla, ahora si estructural: este modulo nunca llama a `preventDefault()` y nunca
 * resuelve `null` ni `undefined`; devuelve siempre un modulo real, un aviso, o una
 * promesa colgada mientras el documento se reemplaza.
 *
 * Lo que NO se disfraza: si la fabrica resuelve un modulo de verdad pero SIN
 * `default` (un export mal escrito), eso es un bug del codigo, no un deploy nuevo, y se
 * re-lanza con un mensaje que lo dice. Un crash visible es mejor que un aviso falso.
 *
 * Por que no reintentamos el `import()` en la misma pagina: `__vitePreload` marca
 * `seen[dep] = true` ANTES de intentar la carga (el CSS no se vuelve a pedir) y
 * `React.lazy` congela el rechazo en `_status = 2` y lo re-lanza para siempre. El unico
 * arreglo posible es reemplazar el documento completo.
 *
 * OJO al añadir chunks lazy DENTRO del editor: si en el momento del fallo hay un
 * `beforeunload` registrado (hoy no lo hay, porque el modulo que registra el guard es
 * justo el que no cargo), `location.reload()` abre el dialogo nativo del navegador y, si
 * el usuario cancela, el `Suspense` se queda girando para siempre. En ese escenario hay
 * que pasar a aviso manual siempre (`maxReloads: 0`).
 */

/** Clave de la guarda anti-bucle. sessionStorage: el presupuesto es por pestaña. */
export const RELOAD_BUDGET_KEY = "mb:chunk-reload";

/** Ventana de la guarda. Fuera de ella, un deploy posterior vuelve a tener su recarga. */
export const RELOAD_WINDOW_MS = 60_000;

/** Una sola recarga automatica por ventana. Mas que eso es un bucle. */
export const MAX_RELOADS = 1;

/**
 * Lista cerrada de mensajes que significan "el asset ya no esta en el servidor".
 * Todo lo demas se re-lanza intacto: un throw real en tiempo de import del editor
 * (o de `@multiburo/mb-contract-editor`) tiene que seguir llegando a Sentry y no
 * puede disfrazarse de "hay una version nueva". Convertir un crash real en un mensaje
 * falso seria peor que el crash.
 */
const STALE_CHUNK_MESSAGES = [
  "unable to preload css",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "expected a javascript module script",
];

type BudgetStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface LazyWithReloadOptions {
  /** Inyectable para los tests; por defecto recarga el documento de verdad. */
  reload?: () => void;
  /** Inyectable para los tests; por defecto `Date.now`. */
  now?: () => number;
  /**
   * Inyectable para los tests; por defecto `safeSessionStorage`, y solo si de verdad
   * persiste (ver `DURABLE_SESSION_BUDGET`). `null` desactiva el presupuesto.
   */
  storage?: BudgetStorage | null;
  /** Inyectable para los tests; por defecto `navigator.onLine !== false`. */
  isOnline?: () => boolean;
  /** 0 desactiva la recarga automatica y deja solo el aviso manual. */
  maxReloads?: number;
  /**
   * Enlace de salida del aviso, que declara la RUTA y no este helper.
   * Vive aqui porque el helper es de repo: cablear "/contracts" dentro haria
   * que el segundo chunk lazy mostrara un enlace a contratos desde una
   * pantalla sin relacion. Si se omite, el aviso no pinta boton de salida.
   */
  backHref?: string;
  backLabel?: string;
}

const defaultReload = (): void => {
  window.location.reload();
};

const readMessage = (error: unknown): string | null => {
  if (typeof error === "string") return error;
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" ? message : null;
};

/** Solo reconoce fallos de asset ausente. Cualquier otra cosa devuelve `false`. */
export const isStaleChunkError = (error: unknown): boolean => {
  const message = readMessage(error);
  if (!message) return false;
  const normalized = message.toLowerCase();
  return STALE_CHUNK_MESSAGES.some((known) => normalized.includes(known));
};

/**
 * Almacenamiento del presupuesto cuando nadie inyecta uno: `safeSessionStorage`.
 *
 * Aqui ya NO hay try/catch propio. El getter `window.sessionStorage` LANZA
 * (SecurityError) en Edge/Windows con los datos del sitio bloqueados —es el mismo fallo
 * de MB-APP-EXPRESS-151/-182— y absorberlo es justo el trabajo de `utils/safeStorage`.
 * Dos envoltorios del mismo global eran dos sitios donde equivocarse (y el invariante
 * `tests/utils/noRawStorage.test.ts` prohibe el acceso crudo precisamente por eso).
 *
 * Lo que este consumidor añade al helper es la exigencia de DURABILIDAD, y no es un
 * detalle: `safeSessionStorage` cae a un Map en memoria cuando el store real no esta,
 * y ese Map se borra justo en la recarga. Un presupuesto en memoria arrancaria a 0 en
 * cada carga del documento => recarga, recarga, recarga, para siempre. Asi que:
 *
 *  - `isAvailable()` decide si hay presupuesto siquiera. Si no, `null` = no recargar
 *    nunca, exactamente la semantica anterior del `catch`.
 *  - el read-back lee con `getPersistedItem`, que NO consulta el Map: una escritura que
 *    no persiste (modo privado, cuota llena) tiene que verse como lo que es.
 */
const DURABLE_SESSION_BUDGET: BudgetStorage = {
  getItem: (key) => safeSessionStorage.getPersistedItem(key),
  setItem: (key, value) => safeSessionStorage.setItem(key, value),
  removeItem: (key) => safeSessionStorage.removeItem(key),
};

const resolveStorage = (
  provided: BudgetStorage | null | undefined,
): BudgetStorage | null => {
  if (provided !== undefined) return provided;
  return safeSessionStorage.isAvailable() ? DURABLE_SESSION_BUDGET : null;
};

/**
 * Concede como maximo `maxReloads` recargas por ventana y deja la marca en storage.
 *
 * Si el storage no es usable NO se recarga nunca: sin una marca que sobreviva a la
 * recarga no hay forma de saber si ya recargamos, y un contador en memoria vuelve a 0
 * en cada arranque, o sea, recarga infinita.
 */
export const tryConsumeReloadBudget = (
  options: LazyWithReloadOptions = {},
): boolean => {
  const maxReloads = options.maxReloads ?? MAX_RELOADS;
  if (maxReloads <= 0) return false;

  const storage = resolveStorage(options.storage);
  if (!storage) return false;

  const now = (options.now ?? Date.now)();

  try {
    let used = 0;
    const raw = storage.getItem(RELOAD_BUDGET_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { n?: unknown; t?: unknown } | null;
      const stamp = typeof parsed?.t === "number" ? parsed.t : 0;
      const count = typeof parsed?.n === "number" ? parsed.n : 0;
      // Fuera de la ventana el presupuesto se renueva. Un reloj hacia atras deja
      // `now - stamp` negativo y mantiene el contador: preferimos no recargar.
      if (now - stamp < RELOAD_WINDOW_MS) used = count;
    }

    if (used >= maxReloads) return false;

    const next = JSON.stringify({ n: used + 1, t: now });
    storage.setItem(RELOAD_BUDGET_KEY, next);
    // Read-back: hay navegadores (y modos privados) donde `setItem` no lanza pero
    // tampoco persiste. Sin marca verificada, no recargamos.
    return storage.getItem(RELOAD_BUDGET_KEY) === next;
  } catch {
    return false;
  }
};

/**
 * Recoge la marca cuando ya ha caducado. Se llama en cada import exitoso.
 *
 * SOLO borra marcas FUERA de la ventana, y eso es el punto, no un detalle: una version
 * anterior borraba la marca en cualquier import exitoso, y con dos o mas chunks lazy eso
 * es un bucle de recargas infinito. Secuencia: el chunk A carga bien (ya estaba en el
 * grafo de modulos, o su asset si existe) y borra la marca; el chunk B, cuyo asset falta
 * de verdad —deploy parcial, asset perdido, index.html cacheado—, encuentra el
 * presupuesto intacto y vuelve a recargar; tras la recarga, A vuelve a cargar bien y a
 * borrar la marca, y B vuelve a recargar. Para siempre, en cada carga del documento, sin
 * aviso y sin forma de reportarlo.
 *
 * Dentro de la ventana la marca se queda: el usuario ve el aviso manual y pulsa
 * Recargar. Cambiar una recarga automatica por un click es la direccion segura; un bucle
 * de recargas es peor que el crash original.
 *
 * Fuera de la ventana el borrado no concede nada que la propia ventana no concediese ya
 * (`tryConsumeReloadBudget` ignora las marcas caducadas): solo deja limpio el storage.
 */
export const clearReloadBudget = (
  options: LazyWithReloadOptions = {},
): void => {
  const storage = resolveStorage(options.storage);
  if (!storage) return;
  const now = (options.now ?? Date.now)();
  try {
    const raw = storage.getItem(RELOAD_BUDGET_KEY);
    if (raw === null) return;
    const parsed = JSON.parse(raw) as { t?: unknown } | null;
    const stamp = typeof parsed?.t === "number" ? parsed.t : 0;
    // Reloj hacia atras (`now - stamp` negativo): la marca se conserva, igual que en
    // `tryConsumeReloadBudget`. Ante la duda, no devolvemos presupuesto.
    if (now - stamp < RELOAD_WINDOW_MS) return;
    storage.removeItem(RELOAD_BUDGET_KEY);
  } catch {
    /* storage bloqueado o marca ilegible: no hay nada que limpiar con seguridad */
  }
};

const NoticeShell: React.FC<{
  title: string;
  subTitle: string;
  actionLabel: string;
  onAction: () => void;
  /**
   * Enlace de salida, OPCIONAL. Este helper es la envoltura de repo que debe
   * usar todo chunk lazy nuevo (lo exige el invariante), asi que no puede
   * llevar "/contracts" cableado: el segundo adoptante mostraria un enlace a
   * contratos desde una pantalla sin relacion. Si no se da, no se pinta boton.
   */
  backHref?: string;
  backLabel?: string;
}> = ({ title, subTitle, actionLabel, onAction, backHref, backLabel }) => (
  <Result
    status="info"
    title={title}
    subTitle={subTitle}
    extra={[
      <Button key="action" type="primary" onClick={onAction}>
        {actionLabel}
      </Button>,
      // `<a>` y no `<Link>`: la navegacion de react-router reusaria este mismo
      // documento viejo y volveria a fallar. Aqui queremos una carga completa.
      backHref ? (
        <Button key="back" href={backHref}>
          {backLabel ?? "Volver"}
        </Button>
      ) : null,
    ]}
  />
);

/** Aviso del camino agotado. La recarga la dispara el usuario: no puede ciclar. */
export const StaleVersionNotice: React.FC<{
  reload?: () => void;
  backHref?: string;
  backLabel?: string;
}> = ({
  reload = defaultReload,
  backHref,
  backLabel,
}) => (
  <NoticeShell
    title="Hay una versión nueva de la aplicación"
    subTitle="Recarga la página para continuar. No se perdió información guardada."
    actionLabel="Recargar"
    onAction={reload}
    backHref={backHref}
    backLabel={backLabel}
  />
);

/** Sin red, recargar cambiaria la app por la pagina de error del navegador. */
export const OfflineNotice: React.FC<{
  reload?: () => void;
  backHref?: string;
  backLabel?: string;
}> = ({
  reload = defaultReload,
  backHref,
  backLabel,
}) => (
  <NoticeShell
    title="Sin conexión"
    subTitle="No pudimos cargar esta sección. Revisa tu conexión y vuelve a intentarlo."
    actionLabel="Reintentar"
    onAction={reload}
    backHref={backHref}
    backLabel={backLabel}
  />
);

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type AnyComponent = React.ComponentType<any>;

const asModule = <T extends AnyComponent>(
  Component: React.FC<{
    reload?: () => void;
    backHref?: string;
    backLabel?: string;
  }>,
  reload: () => void,
  back?: { href?: string; label?: string },
): { default: T } => ({
  default: (() => (
    <Component reload={reload} backHref={back?.href} backLabel={back?.label} />
  )) as unknown as T,
});

/**
 * Sentry no puede tumbar el camino de recuperacion.
 *
 * `handleChunkFailure` corre DENTRO del manejador de rechazo de la fabrica del lazy: si
 * algo lanza aqui, el lazy se rechaza con ESE error, React lo re-lanza en el render y
 * aterriza en el `Sentry.ErrorBoundary` de `App.tsx`. O sea, un fallo de la propia
 * telemetria crearia un issue nuevo con un mensaje sin relacion con la causa —
 * justo lo que este fix existe para evitar. `addBreadcrumb` es API estable y
 * `Sentry.logger` existe en @sentry/react 10.31.0, pero ninguna de las dos merece poder
 * convertir una recuperacion correcta en un crash: la telemetria es opcional, recuperarse
 * no lo es.
 */
const withoutThrowing = (report: () => void): void => {
  try {
    report();
  } catch {
    /* telemetria caida: el usuario se recupera igual, que es lo unico que importa */
  }
};

/**
 * Camino de recuperacion: recarga con presupuesto, o aviso manual.
 *
 * Esta separado de `handleChunkFailure` porque hay DOS entradas: el rechazo de la fabrica
 * (que antes tiene que pasar el filtro de `isStaleChunkError`) y la red de seguridad del
 * camino de exito, que ya sabe que el modulo resuelto esta vacio y no necesita adivinarlo
 * a partir de un mensaje.
 */
const recoverFromStaleChunk = <T extends AnyComponent>(
  error: unknown,
  options: LazyWithReloadOptions,
): Promise<{ default: T }> => {
  const reload = options.reload ?? defaultReload;
  const isOnline =
    options.isOnline ??
    (() => typeof navigator === "undefined" || navigator.onLine !== false);

  // Solo migajas y logs: un `captureException` aqui sustituiria este issue por otro
  // con los mismos eventos, que es exactamente lo que no queremos.
  withoutThrowing(() =>
    Sentry.addBreadcrumb({
      category: "app.chunk",
      level: "warning",
      message: "Chunk lazy no disponible (probable deploy nuevo)",
      data: { reason: readMessage(error) ?? "sin mensaje" },
    }),
  );

  if (!isOnline()) {
    return Promise.resolve(asModule<T>(OfflineNotice, reload, {
      href: options.backHref,
      label: options.backLabel,
    }));
  }

  if (tryConsumeReloadBudget(options)) {
    try {
      reload();
      // Promesa colgada a proposito: el `Suspense` mantiene su fallback mientras el
      // documento se reemplaza. Nunca resolvemos `undefined`, que es lo que hace que
      // React lea `.default` de nada.
      return new Promise<{ default: T }>(() => {});
    } catch {
      // `reload` inaccesible (iframe sandbox, navegador raro): aviso manual.
      return Promise.resolve(asModule<T>(StaleVersionNotice, reload, {
      href: options.backHref,
      label: options.backLabel,
    }));
    }
  }

  withoutThrowing(() =>
    Sentry.logger.warn("Recarga por chunk agotada, se muestra aviso manual", {
      key: RELOAD_BUDGET_KEY,
      reason: readMessage(error) ?? "sin mensaje",
    }),
  );

  return Promise.resolve(asModule<T>(StaleVersionNotice, reload, {
      href: options.backHref,
      label: options.backLabel,
    }));
};

const handleChunkFailure = <T extends AnyComponent>(
  error: unknown,
  options: LazyWithReloadOptions,
): Promise<{ default: T }> => {
  // Error no reconocido: fuera de aqui tal cual, para que siga llegando a Sentry.
  if (!isStaleChunkError(error)) throw error;
  return recoverFromStaleChunk<T>(error, options);
};

/**
 * Mensaje de la red de seguridad del camino de exito. Explica la causa real, porque el
 * `TypeError` que React produciria en su lugar no la insinua siquiera.
 */
const EMPTY_MODULE_MESSAGE =
  "El import() del chunk resolvio sin modulo (asset ausente y evento de preload cancelado)";

/**
 * Lo que devuelve `__vitePreload` cuando alguien cancelo `vite:preloadError` y el asset
 * tampoco esta. Recibe `unknown` a proposito: el tipo declarado de la fabrica promete un
 * modulo y es justamente esa promesa la que no se cumple.
 */
const isEmptyModule = (loaded: unknown): boolean =>
  loaded === null || loaded === undefined;

/**
 * Un modulo utilizable por `React.lazy` es un objeto (o funcion) con `default`.
 *
 * `in` sobre un primitivo LANZA, asi que la comprobacion de tipo va primero: este
 * predicado corre en el camino de exito de cada chunk y no puede ser el que rompa un
 * import que funcionaba.
 */
const hasDefaultExport = (loaded: unknown): boolean =>
  (typeof loaded === "object" || typeof loaded === "function") &&
  loaded !== null &&
  "default" in (loaded as object);

/**
 * `React.lazy` que sobrevive a un deploy nuevo con la pestaña abierta.
 *
 * Invariante del repo: TODO chunk lazy pasa por aqui. Lo vigilan DOS barridos de
 * `src/tests/utils/lazyWithReload.test.tsx` sobre los ~426 archivos de produccion de
 * `src/`, con los detectores de `src/tests/helpers/bareLazyScan.ts`:
 *
 *  - "ningun React.lazy a pelo": cero referencias a `lazy` de React fuera de ESTE
 *    archivo. Busca la REFERENCIA y no una forma concreta de escribir la fabrica, asi que
 *    `React.lazy(async () => import(...))`, una fabrica nombrada, un alias o un
 *    `require("react").lazy` tambien caen.
 *
 *  - "ningun import() dinamico fuera de lazyWithReload": cero `import()` que no esten
 *    dentro de los argumentos de una llamada a `lazyWithReload`. Este es el que de verdad
 *    cubre el invariante, porque la version anterior solo vigilaba la FORMA: un
 *    `const mod = await import("js/...")` a pelo produce el MISMO chunk, el mismo fallo
 *    tras un deploy y el mismo mensaje, no tiene ningun `lazy` que buscar, y este
 *    envoltorio —que envuelve `React.lazy`— no puede interceptarlo. Ademas falla peor:
 *    sin Suspense ni ErrorBoundary en ese camino, el rechazo acaba en
 *    `onunhandledrejection` y el usuario solo ve un boton muerto.
 *
 * Que los detectores no son placebo se prueba en `src/tests/utils/bareLazyScan.test.ts`,
 * donde se les da de comer LITERALMENTE cada evasion con la que se burlo a sus versiones
 * anteriores (incluido el `await import("js/components/Contracts/PreviewPdf")` que paso
 * en verde) y se exige que las vean.
 */
export function lazyWithReload<T extends AnyComponent>(
  factory: () => Promise<{ default: T }>,
  options: LazyWithReloadOptions = {},
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    // `then(ok, fail)` y no `then().catch()`: asi el manejador no ve nunca los errores
    // que pudiera producir el camino de exito.
    factory().then(
      (loaded) => {
        // RED DE SEGURIDAD. `__vitePreload` RESUELVE `undefined` si alguien cancelo
        // `vite:preloadError` y el chunk tampoco esta: devolverselo a React produce el
        // `TypeError` sobre `.default` que este helper existe para evitar. Mismo sintoma
        // que un chunk ausente => mismo tratamiento.
        if (isEmptyModule(loaded)) {
          return recoverFromStaleChunk<T>(
            new Error(EMPTY_MODULE_MESSAGE),
            options,
          );
        }
        // Un modulo real sin `default` es un export mal escrito, no un deploy nuevo:
        // crash visible con un mensaje que apunta a la causa. Taparlo con "hay una
        // version nueva" mandaria al usuario a recargar para siempre.
        if (!hasDefaultExport(loaded)) {
          throw new TypeError(
            `lazyWithReload: el modulo cargado no tiene export default (recibido ${typeof loaded}). Revisa el export del chunk; esto no es un deploy nuevo.`,
          );
        }
        clearReloadBudget(options);
        return loaded;
      },
      (error: unknown) => handleChunkFailure<T>(error, options),
    ),
  );
}

export default lazyWithReload;
